import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, UIMatch, useLocation, useMatches } from 'react-router-dom';
import styled from 'styled-components';

import { AppPage, LongPullDocumentBuildQueue, NavBar, NgSideNavWithFlyout, NotificationsList } from '../../components';
import { FeedbackWidget } from '../../components/Sentry/FeedbackWidget';
import CircularLoader from '../../shared/components/CircularLoader';
import TrackPageviewEvent from '../../components/TrackPageviewEvent/TrackPageviewEvent';

const StyledNotificationsWrapper = styled.div<{ $sideNavWidth: number; $topNavHeight: number }>`
  position: fixed;
  top: ${({ $topNavHeight }) => ($topNavHeight ? `${$topNavHeight}px` : '60px')};
  left: ${({ $sideNavWidth }) => ($sideNavWidth ? `${$sideNavWidth}px` : '62px')};
  width: ${({ $sideNavWidth }) => `calc(100vw - ${$sideNavWidth ? `${$sideNavWidth}px` : '62px'})`};
  margin: 0 auto;
  z-index: 10;
`;

const StyledContentWrapper = styled.div<{ $contentPadding: string | undefined }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  /* padding-top: ${({ theme }) => theme.spacing.large}; */
  /* padding: ${({ $contentPadding }) => $contentPadding && $contentPadding}; */
`;

const StyledMainGrid = styled.div`
  display: flex;
  min-height: 100vh;
`;

const AssetManagerLayout = () => {
  const location = useLocation();
  const matches = useMatches() as UIMatch<unknown, RouteHandle>[];
  const navBarRef = useRef<HTMLDivElement>(null);
  const sideNavRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const [sideNavWidth, setSideNavWidth] = useState(62); // Default width
  const [topNavbarHeight, setTopNavbarHeight] = useState(0);
  const [notificationsHeight, setNotificationsHeight] = useState(0);

  const appPagePadding = matches
    .filter((match) => Boolean(match.handle?.appPagePadding))
    .at(-1)?.handle?.appPagePadding;

  // region EFFECTS
  useLayoutEffect(() => {
    if (navBarRef?.current?.clientHeight) {
      setTopNavbarHeight(navBarRef.current.clientHeight);
    }
  }, [navBarRef?.current?.clientHeight]);

  useLayoutEffect(() => {
    if (topNavbarHeight > 0) {
      document.documentElement.style.setProperty('--top-nav-height', `${topNavbarHeight}px`);
    }
  }, [topNavbarHeight]);

  useEffect(() => {
    if (!notificationsRef.current) return;

    const element = notificationsRef.current;

    const observer = new ResizeObserver(() => {
      setNotificationsHeight(element.offsetHeight);
    });

    observer.observe(element);

    setNotificationsHeight(element.offsetHeight);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!sideNavRef.current) return;

    const element = sideNavRef.current;

    const observer = new ResizeObserver(() => {
      setSideNavWidth(element.offsetWidth);
      document.documentElement.style.setProperty('--side-nav-width', `${element.offsetWidth}px`);
    });

    observer.observe(element);

    // Set initial width immediately
    setSideNavWidth(element.offsetWidth);
    document.documentElement.style.setProperty('--side-nav-width', `${element.offsetWidth}px`);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (window.scrollY > 0) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [location.pathname]);
  //endregion

  return (
    <>
      <TrackPageviewEvent />
      <NavBar ref={navBarRef} />

      <StyledNotificationsWrapper ref={notificationsRef} $sideNavWidth={sideNavWidth} $topNavHeight={topNavbarHeight}>
        <NotificationsList />
        <LongPullDocumentBuildQueue />
      </StyledNotificationsWrapper>

      <StyledMainGrid style={{ '--top-nav-height': `${topNavbarHeight}px`, '--notifications-height': `${notificationsHeight}px` } as React.CSSProperties}>
        <NgSideNavWithFlyout ref={sideNavRef} topNavbarHeight={topNavbarHeight} />

        <AppPage padding={appPagePadding}>
          <StyledContentWrapper $contentPadding={'0'} style={{ marginTop: notificationsHeight }}>
            <Suspense fallback={<CircularLoader centered />}>
              <Outlet />
            </Suspense>
          </StyledContentWrapper>
        </AppPage>
      </StyledMainGrid>
      <FeedbackWidget />
    </>
  );
};

export default AssetManagerLayout;
